import { constants } from "../constant/constants";
import magicLinkAuthService from "./magic-link-auth-service";
import { MsalService } from "./msal-service";

const msalService = MsalService.getInstance();
export enum AuthType {
    AzureAD = 1,
    MagicLink = 2,
}

function parseStoredAuthType(raw: string | null): AuthType | null {
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed === AuthType.AzureAD || parsed === AuthType.MagicLink) {
            return parsed;
        }
    } catch {
        const numeric = Number(raw.trim());
        if (numeric === AuthType.AzureAD || numeric === AuthType.MagicLink) {
            return numeric;
        }
    }
    return null;
}

export class authService {

    private static authType: AuthType = AuthType.AzureAD;

    private static syncAuthTypeFromStorage(): void {
        const stored = parseStoredAuthType(
            localStorage.getItem(constants.localStorage.keys.authType),
        );
        if (stored !== null) {
            this.authType = stored;
            return;
        }
        if (magicLinkAuthService.isAuthenticated()) {
            this.authType = AuthType.MagicLink;
        }
    }

    public static async init() {
        this.syncAuthTypeFromStorage();

        if (this.getAuthType() == AuthType.AzureAD) {
            await msalService.HandleRedirect();
        } else if (!magicLinkAuthService.isAuthenticated()) {
            const hasStoredAccessToken = Boolean(
                magicLinkAuthService.getAccessTokenFromLocalStorage(),
            );
            const hasStoredExpiry = Boolean(
                localStorage.getItem(constants.localStorage.keys.magicLink.tokenExpiry),
            );
            // Cookie refresh only makes sense if we still have local access-token metadata
            // (expired access token). After logout both are cleared � skip refresh to avoid 400 noise.
            if (hasStoredAccessToken && hasStoredExpiry) {
                await this.getAccessToken();
            }
        }
        /** Authenticated landing route is handled in `legacy-init.ts` after `router.isReady()` so Login never flashes. */
    }

    public static setAuthType(authType: AuthType) {
        this.authType = authType;
        localStorage.setItem(
            constants.localStorage.keys.authType,
            authType.toString(),
        );
    }

    private static hydrateAuthTypeFromStorage(): void {
        const stored = this.getAuthTypeFromLocalStorage();
        if (stored) {
            this.authType = JSON.parse(stored) as AuthType;
        }
    }

    public static getAuthType() {
        this.hydrateAuthTypeFromStorage();
        return this.authType;
    }

     private static getAuthTypeFromLocalStorage() {
        return localStorage.getItem(constants.localStorage.keys.authType);
    }

    public static async getAccessToken() {
        if (this.getAuthType() == AuthType.AzureAD) {
            const response = await msalService.acquireToken();
            return response != null ? response.accessToken : null;
        } else {
            return await magicLinkAuthService.getAccessToken();
        }
    }

    public static getLocallySavedAccessToken() {
        if (this.getAuthType() == AuthType.AzureAD) {
            return msalService.accessToken;
        } else {
            return magicLinkAuthService.getAccessTokenFromLocalStorage();
        }
    }

    public static async logout() {
        if (this.getAuthType() == AuthType.AzureAD) {
            await msalService.signOut();
        } else {
            await magicLinkAuthService.logout();
        }
    }

    public static async signIn() {
        if (this.getAuthType() == AuthType.AzureAD) {
            await msalService.signIn();
        } else {
            await magicLinkAuthService.signIn();
        }
    }

    public static isAuthenticated(): boolean {
        if (this.getAuthType() == AuthType.AzureAD) {
            return msalService.isAuthenticated;
        } else {
            return magicLinkAuthService.isAuthenticated();
        }
    }

    public static GetAuthenticatedUserEmail() {
        if (this.getAuthType() == AuthType.AzureAD) {
            return msalService.emailaddress;
        } else {
            let user = magicLinkAuthService.getAuthUser();
            if (user != null) {
                return user.email as string;
            } else {
                return "";
            }
        }
    }
}
